import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api, { TOKEN_KEY } from "@/lib/api";

const AuthContext = createContext(null);
const EXPIRED_KEY = "axiora_expired";

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [authed, setAuthed] = useState(false);
  const [expired, setExpired] = useState(false);
  const [loading, setLoading] = useState(true);
  const refreshRef = useRef(null);

  const clearRenewal = () => { if (refreshRef.current) clearInterval(refreshRef.current); };

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
    clearRenewal();
  }, []);

  const markExpired = useCallback(() => {
    try { sessionStorage.setItem(EXPIRED_KEY, "1"); } catch (e) { /* noop */ }
    setExpired(true);
    logout();
  }, [logout]);

  const startSilentRenewal = useCallback(() => {
    clearRenewal();
    // Renovación silenciosa cada 12 minutos (sesión máx. 30 min)
    refreshRef.current = setInterval(async () => {
      try {
        const { data } = await api.post("/auth/refresh");
        if (data?.token) localStorage.setItem(TOKEN_KEY, data.token);
      } catch (e) {
        markExpired();
      }
    }, 12 * 60 * 1000);
  }, [markExpired]);

  const login = useCallback((token) => {
    localStorage.setItem(TOKEN_KEY, token);
    try { sessionStorage.removeItem(EXPIRED_KEY); } catch (e) { /* noop */ }
    setExpired(false);
    setAuthed(true);
    startSilentRenewal();
  }, [startSilentRenewal]);

  useEffect(() => {
    const bootstrap = async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) { setLoading(false); return; }
      try {
        await api.get("/auth/me");
        setAuthed(true);
        startSilentRenewal();
      } catch (e) {
        // Había token pero ya no es válido => sesión expirada (no intruso)
        markExpired();
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
    // El interceptor 401 dispara este evento cuando había una sesión activa
    const onUnauth = () => { markExpired(); };
    window.addEventListener("axiora-unauth", onUnauth);
    return () => { window.removeEventListener("axiora-unauth", onUnauth); clearRenewal(); };
  }, [markExpired, startSilentRenewal]);

  return (
    <AuthContext.Provider value={{ authed, expired, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
